import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/hooks/use-toast";
import { Switch } from "@/components/ui/switch";

// Owner-only panel: who, besides the owner, can see sensitive expense lines
// (salaries and doctor commission). Only rendered when the viewer is the
// sensitive admin (the page gates this on data.is_sensitive_admin).
export default function VisibilityPanel({ visibility, onChanged }) {
  const { toast } = useToast();
  const members = Array.isArray(visibility?.members) ? visibility.members : [];

  // Per-row in-flight tracking so each toggle disables only itself.
  const [pendingId, setPendingId] = useState(null);

  const handleToggle = async (member) => {
    const turningOn = !member.has_grant;
    setPendingId(member.user_id);
    try {
      const rpc = turningOn
        ? "grant_sensitive_expense_visibility"
        : "revoke_sensitive_expense_visibility";
      const { error: rpcError } = await supabase.rpc(rpc, { p_user_id: member.user_id });
      if (rpcError) {
        toast({
          title: "Couldn't update visibility",
          description: rpcError.message || "Something went wrong. Try again.",
          variant: "destructive",
        });
        return;
      }
      toast({
        title: turningOn ? "Access granted" : "Access removed",
        description: turningOn
          ? `${member.full_name} can now see sensitive expenses.`
          : `${member.full_name} can no longer see sensitive expenses.`,
      });
      await onChanged?.();
    } catch (err) {
      toast({
        title: "Couldn't update visibility",
        description: err?.message || "Something went wrong. Try again.",
        variant: "destructive",
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <section data-testid="expenses-visibility" className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-start gap-2 border-b border-slate-200 px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-teal-600" strokeWidth={2.25} />
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Who can see sensitive expenses</h2>
          <p className="mt-0.5 text-[12px] text-slate-500">
            Salaries and doctor commission lines are visible only to you and the people you enable here.
          </p>
        </div>
      </div>

      {members.length === 0 ? (
        <div className="px-4 py-6 text-center text-[13px] text-slate-500">
          No other team members to grant access to yet.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {members.map((member) => {
            const isPending = pendingId === member.user_id;
            return (
              <li
                key={member.user_id}
                data-testid="visibility-row"
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-medium text-slate-900">
                    {member.full_name}
                  </div>
                  {member.role ? (
                    <div className="text-[12px] capitalize text-slate-500">
                      {String(member.role).replace(/_/g, " ")}
                    </div>
                  ) : null}
                </div>
                <Switch
                  checked={!!member.has_grant}
                  disabled={isPending}
                  onCheckedChange={() => handleToggle(member)}
                  aria-label={
                    member.has_grant
                      ? `Remove sensitive access for ${member.full_name}`
                      : `Grant sensitive access to ${member.full_name}`
                  }
                />
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
